import { useQuery } from '@tanstack/react-query'
import { storesService } from '@/services/stores'
import { productsService } from '@/services/products'
import { categoriesService } from '@/services/categories'
import type { Store } from '@/services/stores'
import type { Product, ProductStorefrontPayload } from '@/services/products'
import type { Category } from '@/services/categories'
import {
  usePreviewMode,
  usePreviewCatalogId,
  usePreviewTemplateId,
} from '@/storefront/context/PreviewModeContext'
import {
  getPreviewCategories,
  getPreviewProducts,
  getPreviewProductsByCategorySlug,
  getPreviewProductBySlug,
  type PreviewDemoCatalog,
} from '@/data/demoStorefrontData'

/** Build `{ name, values[] }` for PDP `VariantSelectors` from DB-style options or variant attributes. */
function previewStorefrontOptionsFromProduct(p: Product): { name: string; values: string[] }[] {
  if (p.options?.length) {
    return [...p.options]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((o) => ({
        name: o.name,
        values: [...(o.values ?? [])]
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((v) => v.value),
      }))
  }
  const vars = p.variants ?? []
  if (!vars.length) return []
  const map = new Map<string, Set<string>>()
  for (const v of vars) {
    const attrs = (v.attributes ?? {}) as Record<string, string>
    for (const [k, val] of Object.entries(attrs)) {
      const t = String(val ?? '').trim()
      if (!t) continue
      if (!map.has(k)) map.set(k, new Set())
      map.get(k)!.add(t)
    }
  }
  return [...map.entries()].map(([name, set]) => ({
    name,
    values: [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
  }))
}

/** Preview hooks return flat `Product`; PDP expects `ProductStorefrontPayload` like the live API. */
function previewProductToStorefrontPayload(p: Product): ProductStorefrontPayload {
  const variants = p.variants ?? []
  return {
    product: p as Product & { images?: { url: string; position: number }[] },
    default_variant: p.default_variant_id ?? variants.find((v) => v.is_default)?.id ?? variants[0]?.id ?? null,
    options: previewStorefrontOptionsFromProduct(p),
    variants,
    variant_lookup: {},
  }
}

export function useReferenceStoreBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['reference-store', slug],
    queryFn: async (): Promise<Store | null> => {
      if (!slug) return null
      return storesService.getBySlug(slug, { bypassCache: true })
    },
    enabled: !!slug,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Do not retry 4xx errors (store not found, forbidden) — they won't self-heal.
    // Only retry network/5xx failures (up to 2 times).
    retry: (failureCount, error) => {
      const status = (error as any)?.status ?? (error as any)?.statusCode ?? 0
      if (status >= 400 && status < 500) return false
      return failureCount < 2
    },
  })
}

/** Preview catalog queries must include template id so caches never bleed across `/preview-template/:id`. */
function previewCatalogQueryPrefix(
  storeId: string | undefined,
  catalogId: PreviewDemoCatalog,
  previewTemplateId: string | null,
) {
  return ['preview', storeId ?? '', catalogId, previewTemplateId ?? ''] as const
}

/** @deprecated Wire-up reserved for future prefetch-from-grid (no-op for now). */
export function useQuickViewProductPrefetch() {
  return {
    prefetch: (_productId: string) => {
      /* reserved — optional React Query prefetch */
    },
  }
}

export type UseReferenceProductsInput =
  | string
  | undefined
  | { storeId: string | undefined; ids: string[] }

export function useReferenceProducts(input: UseReferenceProductsInput) {
  const isPreview = usePreviewMode()
  const catalogId = usePreviewCatalogId()
  const previewTemplateId = usePreviewTemplateId()

  const legacyMode = typeof input !== 'object' || input === null
  const storeId = legacyMode ? (input as string | undefined) : input.storeId
  const filterIds = legacyMode ? null : [...new Set((input as { ids: string[] }).ids.filter(Boolean))]
  const singleId = !legacyMode && filterIds && filterIds.length === 1 ? filterIds[0] : null
  const multiIds = !legacyMode && filterIds && filterIds.length > 1 ? filterIds : null
  const idKey = filterIds?.length ? [...filterIds].sort().join(',') : 'all'

  return useQuery({
    queryKey: isPreview
      ? [...previewCatalogQueryPrefix(storeId, catalogId, previewTemplateId), 'reference-products', idKey]
      : ['live', 'reference-products', storeId, idKey],
    queryFn: async (): Promise<Product[]> => {
      if (!storeId) return []
      if (singleId) {
        if (isPreview) {
          const all = getPreviewProducts(catalogId)
          const p = all.find((x) => x.id === singleId)
          return p ? [p] : []
        }
        // Public vitrine: shoppers are anonymous — `getById` requires seller auth and fails.
        const payload = await productsService.getProductForStorefront(singleId, storeId)
        return payload ? [productsService.mergeStorefrontPayloadToProduct(payload)] : []
      }
      if (multiIds?.length) {
        if (isPreview) {
          const all = getPreviewProducts(catalogId)
          const set = new Set(multiIds)
          return all.filter((p) => set.has(p.id))
        }
        const rows = await Promise.all(
          multiIds.map(async (id) => {
            const payload = await productsService.getProductForStorefront(id, storeId!)
            return payload ? productsService.mergeStorefrontPayloadToProduct(payload) : null
          }),
        )
        return rows.filter((p): p is Product => p != null)
      }
      if (isPreview) return getPreviewProducts(catalogId)
      return productsService.getByStoreId(storeId)
    },
    enabled:
      !!storeId &&
      (legacyMode || (!!filterIds && filterIds.length > 0)),
    staleTime: isPreview ? Infinity : 30_000,
  })
}

export function useReferenceProductsByCategory(storeId: string | undefined, categorySlug: string | undefined) {
  const isPreview = usePreviewMode()
  const catalogId = usePreviewCatalogId()
  const previewTemplateId = usePreviewTemplateId()
  return useQuery({
    queryKey: isPreview
      ? [
          ...previewCatalogQueryPrefix(storeId, catalogId, previewTemplateId),
          'reference-products-cat',
          categorySlug ?? '',
        ]
      : ['live', 'reference-products-cat', storeId, categorySlug],
    queryFn: (): Promise<Product[]> => {
      if (!storeId) return Promise.resolve([])
      if (isPreview) return Promise.resolve(getPreviewProductsByCategorySlug(catalogId, categorySlug))
      if (!categorySlug) return Promise.resolve([])
      return productsService.getProductsByCategory(storeId, categorySlug)
    },
    enabled: !!storeId && (isPreview || !!categorySlug),
    staleTime: isPreview ? Infinity : 30_000,
  })
}

export function useReferenceCategories(storeId: string | undefined) {
  const isPreview = usePreviewMode()
  const catalogId = usePreviewCatalogId()
  const previewTemplateId = usePreviewTemplateId()
  return useQuery({
    queryKey: isPreview
      ? [...previewCatalogQueryPrefix(storeId, catalogId, previewTemplateId), 'reference-categories']
      : ['live', 'reference-categories', storeId],
    queryFn: (): Promise<Category[]> => {
      if (!storeId) return Promise.resolve([])
      if (isPreview) return Promise.resolve(getPreviewCategories(catalogId))
      return categoriesService.getByStore(storeId)
    },
    enabled: !!storeId,
    staleTime: isPreview ? Infinity : 60_000,
  })
}

export function useReferenceProductDetail(storeId: string | undefined, productSlug: string | undefined) {
  const isPreview = usePreviewMode()
  const catalogId = usePreviewCatalogId()
  const previewTemplateId = usePreviewTemplateId()
  return useQuery({
    queryKey: isPreview
      ? [
          ...previewCatalogQueryPrefix(storeId, catalogId, previewTemplateId),
          'reference-pdp',
          productSlug ?? '',
        ]
      : ['live', 'reference-pdp', storeId, productSlug],
    queryFn: async () => {
      if (!storeId) return null
      if (isPreview) {
        const p = getPreviewProductBySlug(catalogId, productSlug)
        return p ? previewProductToStorefrontPayload(p) : null
      }
      if (!productSlug) return null
      return productsService.getProductForStorefront(productSlug, storeId)
    },
    enabled: !!storeId && (isPreview || !!productSlug),
    staleTime: isPreview ? Infinity : 15_000,
  })
}
